import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, BarChart3, Settings, Megaphone, Check, X } from "lucide-react";
import { useVisualMode } from "../../utils/visualMode.js";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";


const STORAGE_KEY = "buukkeri.cookieConsent.v1";
const GA_ID = process.env.REACT_APP_GA_ID;

// Pieni muistutus “kevyt ulkoasu” -tilasta
const VISUAL_HINT_KEY = "buukkeri.visualHint.seen";
const VISUAL_HINT_DELAY = 45000; // ms → 45 sekuntia


// Oletukset (voit säätää). Huom: varsinainen voimassa oleva tila muodostuu
// tallennetusta + käyttäjän klikkauksesta. Pelkkä oletus ei vielä muuta Consent Modea.
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: true,
  marketing: true,
  preferences: true,
};

/**
 * Lataa GA-tag (vain jos sitä ei ole jo). WP:n head saattaa jo ladata gtagin,
 * jolloin tämä funktio palaa heti.
 */
function loadGAOnce(id) {
  if (!id) return;
  if (window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  // Perusinit. WP:n puolella on lisäksi oma gtag('config', ...) anonymize_ip:llä.
  gtag("js", new Date());
  gtag("config", id);
}

/**
 * Päivitä Consent Mode v2 → ei vaadi GA_ID:tä (WP:n gtag riittää).
 * Aseta ga-disable-* vain jos ID löytyy.
 */
function applyGAConsent(consent) {
  if (typeof window.gtag !== "function") return;

  window.gtag("consent", "update", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",

    // Alla olevat eivät ole GA:n pakollisia, mutta voi olla hyödyllisiä ekosysteemissä
    functionality_storage: consent.preferences ? "granted" : "denied",
    personalization_storage: consent.preferences ? "granted" : "denied",
    security_storage: "granted",
  });

  if (GA_ID) {
    // Tämä flag estää GA-kirjoituksen täysin, jos analytics=false
    window[`ga-disable-${GA_ID}`] = !consent.analytics;
  }
}

// Lähetä Consent Mode v2 -päivitys WP:n kuuntelijalle
function dispatchConsentUpdate(consent) {
  try {
    window.dispatchEvent(
      new CustomEvent("consent:update", {
        detail: {
          analytics_storage: consent.analytics ? "granted" : "denied",
          ad_storage: consent.marketing ? "granted" : "denied",
          ad_user_data: consent.marketing ? "granted" : "denied",
          ad_personalization: consent.marketing ? "granted" : "denied",
        },
      })
    );
  } catch {}
}

/**
 * Ammu varmistava page_view heti suostumuksen jälkeen (Realtime herää).
 * WP:n SPA-hookit hoitaa myöhemmät navigoinnit.
 */
function firePV() {
  try {
    if (typeof window.gtag !== "function") return;
    const { href, pathname, search } = window.location; // <- ei varoitusta
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: href,
      page_path: pathname + search,
    });
  } catch (_) {}
}


export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [showManageBtn, setShowManageBtn] = useState(false);
  const [showVisualHint, setShowVisualHint] = useState(false);

  // Sivun ulkoasun kevyt/tehoste-moodi (ei vaikuta GA-consenttiin)
  const { isMinimal, setMode } = useVisualMode();

  // Muistutuksen piilottaminen ja merkitseminen nähdyksi
  const dismissVisualHint = () => {
    setShowVisualHint(false);
    try {
      localStorage.setItem(VISUAL_HINT_KEY, "1");
    } catch {}
  };

  // Näytä pieni “kevyt ulkoasu” -vihje kerran, kun:
  //  - evästebanneri on kiinni
  //  - kevyt tila EI ole päällä
  //  - vihjettä ei ole aiemmin nähty
  useEffect(() => {
    if (open) return;
    if (isMinimal) return;

    let alreadySeen = false;
    try {
      alreadySeen = localStorage.getItem(VISUAL_HINT_KEY) === "1";
    } catch {}
    if (alreadySeen) return;

    const t = setTimeout(() => {
      setShowVisualHint(true);
    }, VISUAL_HINT_DELAY);

    return () => clearTimeout(t);
  }, [open, isMinimal]);


  // Bannerin mittaus
  const bannerRef = useRef(null);

  const dispatchCookieHeight = (h) => {
    window.dispatchEvent(new CustomEvent("cookie-banner-height", { detail: { height: h } }));
  };

  const closeBannerAndSignal = () => {
    setOpen(false);
    setShowManageBtn(true);
    dispatchCookieHeight(0);
    window.dispatchEvent(new CustomEvent("cookie-consent-closed"));
  };

  // 1) Lue tallennettu consent (avaa banneri jos ei ole tallennettu)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setConsent({ ...DEFAULT_CONSENT, ...parsed });
        setOpen(false);
        setShowManageBtn(true);
      } else {
        setConsent(DEFAULT_CONSENT);
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  // 2) Jos tallennettu suostumus löytyy → sovella GA-consent ja ammu varmistava PV
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      // Lataa GA vain jos ID annettu ja analytics on päällä
      if (parsed.analytics && GA_ID) loadGAOnce(GA_ID);
      // Päivitä Consent Mode joka tapauksessa (WP:n gtag riittää)
      if (typeof window.gtag === "function") {
        applyGAConsent(parsed);
        // Jos analytics oli jo hyväksytty aiemmin, lähetä varmistava PV ensilatauksella
        if (parsed.analytics) firePV();
      }
    } catch {}
  }, []);

  // 3) Mittaa bannerin korkeus (UI)
  useEffect(() => {
    if (!open) return;
    const send = () => {
      const h = bannerRef.current ? bannerRef.current.offsetHeight : 0;
      dispatchCookieHeight(h);
    };
    send();
    const ro = new ResizeObserver(send);
    if (bannerRef.current) ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [open]);

  // --- Handlereita ---

  // Hyväksy kaikki → grant kaikille, päivitä consent, lähetä PV
  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true, preferences: true };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    setConsent(all);

    if (GA_ID) loadGAOnce(GA_ID);
    if (typeof window.gtag === "function") {
      applyGAConsent(all);
      firePV();
    // Ilmoita WP:n head-skriptelle Consent Moden päivitys
    dispatchConsentUpdate(all);
    }
    closeBannerAndSignal();
  };

  // Vain välttämättömät → analytics/marketing pois, ei PV:tä
  const handleOnlyNecessary = () => {
    const nec = { necessary: true, analytics: false, marketing: false, preferences: false };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nec));
    setConsent(nec);

    if (typeof window.gtag === "function") {
      applyGAConsent(nec);
    }
    // Ilmoita WP:n head-skriptelle Consent Moden päivitys
    dispatchConsentUpdate(nec);
    closeBannerAndSignal();
  };

  // Tallenna valinnat → jos analytics on päällä, ammu PV
  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));

    if (consent.analytics && GA_ID) loadGAOnce(GA_ID);
    if (typeof window.gtag === "function") {
      applyGAConsent(consent);
      if (consent.analytics) firePV();
    }
    // Ilmoita WP:n head-skriptelle Consent Moden päivitys
    dispatchConsentUpdate(consent);
    closeBannerAndSignal();
  };

  const ToggleRow = ({ icon: Icon, label, k }) => (
    <label className="flex items-center justify-between gap-3 py-2 px-3 rounded-xl bg-white/40">
      <span className="flex items-center gap-3">
        <Icon className="h-5 w-5" />
        <span className="font-semibold">{label}</span>
      </span>
      <input
        type="checkbox"
        className="h-5 w-5 accent-pink-600"
        checked={!!consent[k]}
        disabled={k === "necessary"}
        onChange={(e) => setConsent((c) => ({ ...c, [k]: e.target.checked }))}
      />
    </label>
  );

  return (
    <>
      {/* Kelluva vasen hallintanappi (ei näy, kun banneri on auki) */}
      {showManageBtn && !open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-[70] inline-flex items-center gap-2 rounded-full h-11 px-4
                     bg-electric/20 text-white/70 shadow-xl hover:brightness-110
                     hover:bg-electric/80 hover:text-white"
          style={{ bottom: 24, left: 24 }}
        >
          <Cookie className="h-5 w-5" /> Evästeet
        </button>
      )}

      {/* Banneri / modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-x-0 bottom-0 z-[70] p-3"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
          >
            <div
              ref={bannerRef}
              className="
                mx-auto max-w-3xl rounded-2xl ring-1 shadow-2xl
                bg-gradient-to-br from-yellow-200/90 via-pink-200/90 to-fuchsia-200/90 text-black/80 ring-white/50
                dark:bg-gradient-to-br dark:from-night-900/90 dark:via-night-800/75 dark:to-night-700/80
                dark:text-white/90 dark:ring-white-700/70 backdrop-blur-md
              "
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <Cookie className="h-6 w-6 text-pink-700 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-customFont2 text-xl text-black/90 dark:text-white/80">
                      Hyppää bileisiin – anna evästeiden rullata! 🎉
                    </h3>
                    <p className="text-black/70 dark:text-white/80 mt-1">
                      Käytämme evästeitä sivun toimintaan, tilastointiin ja markkinointiin.
                    </p>
                  </div>
                  <button
                    onClick={closeBannerAndSignal}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/10 hover:bg-black/20
                               dark:bg-white/80 dark:text-night-900 dark:hover:bg-white"
                    aria-label="Sulje"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-black/70 hover:text-black/90 dark:text-white/80">
                    Näytä asetukset
                  </summary>
          <div className="grid grid-cols-1 gap-2 mt-3">
            <ToggleRow icon={Check} label="Välttämättömät (aina päällä)" k="necessary" />
            <ToggleRow icon={BarChart3} label="Analytiikka (Google Analytics)" k="analytics" />
            <ToggleRow icon={Megaphone} label="Markkinointi" k="marketing" />
            <ToggleRow icon={Settings} label="Mieltymykset" k="preferences" />
          </div>

          {/* Sivun ulkoasun "kevyt tila" – ei muuta evästeitä, vain visuaalisia tehosteita */}
          <div className="mt-4 rounded-xl bg-white/40 dark:bg-night-800/70 px-3 py-3 flex items-center justify-between gap-3">
            <div className="text-sm text-black/80 dark:text-white/80">
              <p className="font-semibold">
                Kevyt ulkoasu (suorituskykyisempi)
              </p>
              <p className="text-xs mt-0.5 opacity-80">
                Poistaa raskaimmat varjot ja animaatiot hitaille laitteille.
              </p>
            </div>
          <button
            type="button"
            onClick={() => setMode(isMinimal ? "full" : "minimal")}
            className={`
              relative inline-flex h-7 w-12 items-center rounded-full
              transition-colors duration-200
              ${isMinimal
                ? "bg-emerald-500/90 dark:bg-emerald-400/90" // vihreä päällä
                : "bg-night-900/90 dark:bg-night-700"}        // tumma pois päältä
            `}
            aria-pressed={isMinimal}
          >
            <span
              className={`
                inline-block h-5 w-5 transform rounded-full bg-white shadow
                transition-transform duration-200
                ${isMinimal ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>


          </div>
                </details>

                <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <NeonButton
                    type="button"
                    onClick={handleOnlyNecessary}
                    className="!h-auto !rounded-xl !px-4 !py-2 !text-sm
                              !bg-lime-300/50 hover:!bg-black/15
                              dark:!bg-night-700 dark:!text-white dark:hover:!bg-night-600"
                  >
                    Salli vain välttämättömät
                  </NeonButton>

                  <NeonButton
                    type="button"
                    onClick={handleAcceptAll}
                    className="!h-auto !rounded-xl !px-4 !py-2 !text-sm
                              !bg-cyan-500 !text-white hover:!brightness-110
                              dark:!bg-pink-600 dark:hover:!bg-pink-500"
                  >
                    Hyväksy kaikki
                  </NeonButton>

                  <NeonButton
                    type="button"
                    onClick={handleSave}
                    className="!h-auto !rounded-xl !px-4 !py-2 !text-sm
                              !bg-primary/70 hover:!bg-white
                              dark:!bg-electric/70 dark:!text-white dark:hover:!bg-electric/60"
                  >
                    Tallenna valinnat
                  </NeonButton>
                </div>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Kevyt ulkoasu -muistutus (näkyy vain kerran per selain) */}
      <AnimatePresence>
        {showVisualHint && !open && (
          <motion.div
            className="fixed z-[69] bottom-24 right-4 max-w-xs"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
          <div className="rounded-2xl dark:bg-slate-800 bg-electric/100 ring-1 text-black/90 dark:text-white/90 shadow-lg border border-white/10 p-3 text-xs">
            <div className="flex items-start gap-2">
              <Cookie className="h-4 w-4 mt-0.5 text-primary" />
              <div className="flex-1">
                <p className="font-semibold">
                  Sivu takkuaa?
                </p>
                <p className="text-xs mt-0.5 opacity-80">
                  Voit keventää ulkoasua ja poistaa raskaimmat varjot
                  &amp; animaatiot yhdellä klikkauksella.
                </p>

                <div className="mt-2 flex gap-2">
                  <NeonButton
                    onClick={() => {
                      setMode("minimal");
                      dismissVisualHint();
                    }}
                    className="!text-xs !px-3 !py-1.5 !h-auto"
                  >
                    Ota kevyt tila käyttöön
                  </NeonButton>

                  <NeonButton
                    type="button"
                    onClick={dismissVisualHint}
                    className="!text-xs !px-3 !py-1.5 !h-auto !bg-white/10 !text-white/90 hover:!bg-white/15"
                  >
                    Sulje
                  </NeonButton>

                </div>
              </div>
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}
